/**
 * When things happen for a customer: what time it is where they live, when their next bank draft
 * comes out, and what they pay a month. Pure; tested in schedule.test.ts.
 */
import { PAYMENTS_PER_YEAR, type PremiumMode } from "./domain";

/**
 * The time zone most of a state's numbers ring in. States split across two zones take the one most
 * of their people live in (Florida's panhandle is Central; the peninsula, where the leads are, is
 * Eastern). Good enough to keep a dial out of quiet hours, which is what it is for.
 */
const STATE_ZONES: Record<string, string> = {
  AL: "America/Chicago", AK: "America/Anchorage", AZ: "America/Phoenix", AR: "America/Chicago",
  CA: "America/Los_Angeles", CO: "America/Denver", CT: "America/New_York", DE: "America/New_York",
  DC: "America/New_York", FL: "America/New_York", GA: "America/New_York", HI: "Pacific/Honolulu",
  ID: "America/Boise", IL: "America/Chicago", IN: "America/Indiana/Indianapolis", IA: "America/Chicago",
  KS: "America/Chicago", KY: "America/New_York", LA: "America/Chicago", ME: "America/New_York",
  MD: "America/New_York", MA: "America/New_York", MI: "America/Detroit", MN: "America/Chicago",
  MS: "America/Chicago", MO: "America/Chicago", MT: "America/Denver", NE: "America/Chicago",
  NV: "America/Los_Angeles", NH: "America/New_York", NJ: "America/New_York", NM: "America/Denver",
  NY: "America/New_York", NC: "America/New_York", ND: "America/Chicago", OH: "America/New_York",
  OK: "America/Chicago", OR: "America/Los_Angeles", PA: "America/New_York", RI: "America/New_York",
  SC: "America/New_York", SD: "America/Chicago", TN: "America/Chicago", TX: "America/Chicago",
  UT: "America/Denver", VT: "America/New_York", VA: "America/New_York", WA: "America/Los_Angeles",
  WV: "America/New_York", WI: "America/Chicago", WY: "America/Denver", PR: "America/Puerto_Rico",
}; // prettier-ignore

export function stateTimeZone(state: string | null | undefined): string | null {
  return (state && STATE_ZONES[state.toUpperCase()]) || null;
}

/**
 * The hour on the customer's clock, and whether a sales call is allowed then. The TCPA's quiet
 * hours are before 8 AM and from 9 PM, local to the person called.
 */
export function localCallWindow(
  state: string | null | undefined,
  at: Date = new Date(),
): { time: string; hour: number; minutesLeft: number; open: boolean } | null {
  const zone = stateTimeZone(state);
  if (!zone) return null;
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: zone,
    hour: "numeric",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(at);
  const hour = Number(parts.find((p) => p.type === "hour")?.value ?? 0);
  const minute = Number(parts.find((p) => p.type === "minute")?.value ?? 0);
  const time = at.toLocaleTimeString("en-US", {
    timeZone: zone,
    hour: "numeric",
    minute: "2-digit",
  });
  const open = hour >= 8 && hour < 21;
  return { time, hour, minutesLeft: open ? (21 - hour) * 60 - minute : 0, open };
}

/** What the customer pays a month, from the modal premium or, failing that, the annual one. */
export function monthlyPremium(
  modal: number | null,
  mode: PremiumMode | null,
  annual: number | null,
): number | null {
  if (modal !== null && mode)
    return Math.round(((modal * PAYMENTS_PER_YEAR[mode]) / 12) * 100) / 100;
  return annual !== null ? Math.round((annual / 12) * 100) / 100 : null;
}

/**
 * The next bank draft on or after `today` (all YYYY-MM-DD). The first draft is the effective date;
 * after that, the draft day each month, moved back to the month's last day when the month is short.
 */
export function nextDraftDate(
  draftDay: number | null,
  effectiveDate: string | null,
  today: string,
): string | null {
  if (effectiveDate && effectiveDate >= today) return effectiveDate;
  if (!draftDay) return null;
  const [y, m] = today.split("-").map(Number) as [number, number];
  for (let i = 0; i < 2; i++) {
    const year = y + Math.floor((m - 1 + i) / 12);
    const month = ((m - 1 + i) % 12) + 1;
    const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
    const day = Math.min(draftDay, last);
    const iso = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    if (iso >= today) return iso;
  }
  return null;
}
